// Client-side NatureServe Explorer lookup. Public, no-auth API; the browser calls it directly
//
// Everything returned here is presented to the user under NatureServe's name, so this module must
// never substitute Titan-side input for a missing API value, and must report what it could not
// establish (an inexact taxon match, an unmatched state) rather than papering over it.
// This is intentional because we are citing NatureServe as the 'source of truth' and do not want
// to misrepresent their data.
const BASE = 'https://explorer.natureserve.org'
const LOOKUP_TIMEOUT_MS = 12000

const stripHtml = (s) => (s || '')
  .replace(/<br\s*\/?>|<\/(p|div|li|tr)>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/\n{3,}/g, '\n\n')
  .trim()

const DESC_KEYS = ['nameEn', 'name', 'descEn', 'desc']
const descOf = (o) => {
  if (o === null || o === undefined) return ''
  if (typeof o !== 'object') return o
  const named = DESC_KEYS.find(key => typeof o[key] === 'string' && o[key].trim())
  if (named) return o[named]
  return Object.values(o).find(v => typeof v === 'string') ?? ''
}

// Failures carry a `code` ('timeout' | 'connection' | 'service') so the slide-over can show a
// message that matches the actual problem instead of the generic no-match text.
async function fetchJson (url, options) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS)
  let res
  try {
    res = await fetch(url, { ...options, signal: controller.signal })
  } catch (e) {
    throw Object.assign(new Error('NatureServe request failed'),
      { code: e?.name === 'AbortError' ? 'timeout' : 'connection' })
  } finally {
    clearTimeout(timer)
  }
  if (!res.ok) throw Object.assign(new Error(`NatureServe HTTP ${res.status}`), { code: 'service' })
  try {
    return await res.json()
  } catch {
    throw Object.assign(new Error('NatureServe bad response'), { code: 'service' })
  }
}

async function speciesSearch (searchToken) {
  const data = await fetchJson(`${BASE}/api/data/speciesSearch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      criteriaType: 'species',
      textCriteria: [{ paramType: 'quickSearch', searchToken }],
      pagingOptions: { page: 0, recordsPerPage: 5 }
    })
  })
  return data.results || []
}

// Used to determine if it was a precise hit or the closest match.
function pickTaxon (hits, searchToken) {
  const lc = searchToken.toLowerCase()
  const exact = hits.find(h => h.scientificName?.toLowerCase() === lc || h.primaryCommonName?.toLowerCase() === lc)
  const hit = exact || hits[0]
  if (!hit?.uniqueId) return null
  return {
    id: hit.uniqueId,
    exact: !!exact,
    searchedFor: searchToken,
    matchedName: hit.scientificName || hit.primaryCommonName || null
  }
}

// Searches for scientific name first (unambiguous whereas common names collide),
// then the genus + species binomial (first two of scientific name), then the common name.
async function resolveTaxon (scientific, common) {
  const scientificToken = (scientific || '').trim()
  const commonToken = (common || '').trim()
  // A pasted element id addresses one record directly, so nothing is being matched by name.
  if (/^ELEMENT_GLOBAL\./i.test(scientificToken)) {
    return { id: scientificToken, exact: true, searchedFor: scientificToken, matchedName: null }
  }

  if (scientificToken) {
    const hits = await speciesSearch(scientificToken)
    if (hits.length) return pickTaxon(hits, scientificToken)

    const isCommonNameStandIn = commonToken.toLowerCase() === scientificToken.toLowerCase()
    const binomial = scientificToken.split(/\s+/).slice(0, 2).join(' ')
    if (!isCommonNameStandIn && binomial && binomial.toLowerCase() !== scientificToken.toLowerCase()) {
      const fallbackHits = await speciesSearch(binomial)
      if (fallbackHits.length) {
        const picked = pickTaxon(fallbackHits, binomial)
        // Truncating to the binomial drops a subspecies or variety epithet, so the record found is
        // never the taxon that was asked for — even when the trimmed token matched exactly.
        return picked && { ...picked, exact: false, searchedFor: scientificToken }
      }
    }
  }

  if (commonToken && commonToken.toLowerCase() !== scientificToken.toLowerCase()) {
    const hits = await speciesSearch(commonToken)
    if (hits.length) return pickTaxon(hits, commonToken)
  }

  return null
}

function stateRankFor (taxon, stateCode) {
  const code = (stateCode || '').trim().toUpperCase().replace(/^US-/, '')
  if (!code) return null
  const nationals = taxon.elementNationals || []
  const usNationals = nationals.filter(n => (n.nation?.nationCode || '').toUpperCase() === 'US')
  for (const national of (usNationals.length ? usNationals : nationals)) {
    const match = (national.elementSubnationals || [])
      .find(s => (s.subnation?.subnationCode || '').toUpperCase() === code)
    if (!match) continue
    const subnationCode = match.subnation.subnationCode
    if (match.srank) return { rank: match.srank, rounded: false, subnationCode }
    if (match.roundedSRank) return { rank: match.roundedSRank, rounded: true, subnationCode }
    return null
  }
  return null
}

const HABITAT_REALMS = {
  Terrestrial: 'speciesTerrestrialHabitats',
  Palustrine: 'speciesPalustrineHabitats',
  Riverine: 'speciesRiverineHabitats',
  Lacustrine: 'speciesLacustrineHabitats',
  Estuarine: 'speciesEstuarineHabitats',
  Marine: 'speciesMarineHabitats',
  Subterranean: 'speciesSubterraneanHabitats'
}

// Each entry nests its descriptor under a realm-specific key (e.g. terrestrialHabitat).
function habitatTypeOf (entry) {
  if (!entry || typeof entry !== 'object') return ''
  const nested = Object.values(entry).find(v => v && typeof v === 'object')
  return descOf(nested) || ''
}

// The realm keys are our own labels for the fields we read, not values returned by the API — the
// slide-over routes them through i18n.
function habitatGroups (speciesCharacteristics) {
  const groups = []
  for (const [realm, key] of Object.entries(HABITAT_REALMS)) {
    const arr = speciesCharacteristics[key] || []
    if (!arr.length) continue
    const types = arr.map(habitatTypeOf).filter(Boolean)
    if (types.length) groups.push({ realm, types })
  }
  return groups
}

// Only one of species.common / species.scientific is required for lookup
export async function lookupSpeciesHabitat ({ common, scientific } = {}, stateCode = null) {
  const resolved = await resolveTaxon(scientific, common)
  if (!resolved) return null

  const taxon = await fetchJson(`${BASE}/api/data/taxon/${resolved.id}`, { headers: { Accept: 'application/json' } })
  const sc = taxon.speciesCharacteristics || {}
  const ac = taxon.animalCharacteristics || {}
  const sg = taxon.speciesGlobal || {}

  return {
    commonName: taxon.primaryCommonName || null,
    scientificName: taxon.scientificName || null,
    elementId: taxon.uniqueId || (typeof resolved.id === 'string' ? resolved.id : null),
    match: { exact: resolved.exact, searchedFor: resolved.searchedFor, matchedName: resolved.matchedName },
    informalTaxonomy: sg.informalTaxonomy || null,
    globalRank: taxon.grank || null,
    roundedGlobalRank: taxon.roundedGRank || null,
    stateRank: stateRankFor(taxon, stateCode),
    esaStatus: descOf(sg.usesa) || sg.interpretedUsesa || null,
    majorHabitat: descOf(ac.majorHabitat) || null,
    habitatGroups: habitatGroups(sc),
    habitatComments: stripHtml(sc.habitatComments) || null,
    degreeOfThreat: taxon.rankInfo?.threatImpactAssigned?.threatImpactAssignedDescEn || null,
    keyThreats: stripHtml(taxon.rankInfo?.threatImpactComments) || null,
    grankReviewDate: taxon.grankReviewDate || null,
    lastModified: taxon.lastModified || null,
    url: taxon.nsxUrl ? BASE + taxon.nsxUrl : null
  }
}
